const SECONDARY_CATEGORY_COLOURS = {
  'emerald': '#006d4a',
  'topaz': '#ee7234',
  'ruby': '#9c0001',
  'sapphire': '#0159a3',
  'amethyst': '#3d3292',
  'onyx': '#211f20',
  'chase': '#dec05e',
  'gold': '#dec05e',
  // add more as needed
};

class TierShowcase extends HTMLElement {
  constructor() {
    super();
    this.state = {
      config: null,
      colsPerRow: 4,
      selectedPrimaryFilters: new Set(),
      selectedSecondaryFilters: new Set(),
      selectedTertiaryFilters: new Set(),
      activeVideoTab: 'primary',
      filteredItems: [],
      swiperInstance: null,
      lastViewportWidth: window.innerWidth
    };
    this.resizeObserver = null;
  }

 connectedCallback() {
  this.state.colsPerRow = parseInt(this.dataset.cols) || 4;

  this.init();
  this.setupResizeListener();
  this.moveNavigationButton();
}

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  moveNavigationButton() {
    // Wait for config to be available
    if (!this.state.config) {
      setTimeout(() => this.moveNavigationButton(), 100);
      return;
    }

    const { enableTierNavigationButton, tierNavigationButtonLabel, sectionId } = this.state.config;

    if (!enableTierNavigationButton) return;

    // Find the product__info-wrapper
    const infoWrapper = document.querySelector('.product__info-wrapper');
    if (!infoWrapper) {
      console.warn('Tier Showcase: product__info-wrapper not found');
      return;
    }

    // Find the wishlists-social-proof element
    const wishlistsProof = infoWrapper.querySelector('wishlists-social-proof');
    if (!wishlistsProof) {
      console.warn('Tier Showcase: wishlists-social-proof not found');
      return;
    }

    // Create the navigation button
    const navButton = document.createElement('button');
    navButton.className = 'tier-showcase-navigation-button-container product-form__submit button button--full-width button--secondary';
    navButton.type = 'button';
    navButton.innerHTML = `
      <span class="tier-showcase-navigation-button-label">${tierNavigationButtonLabel}</span>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    `;

    // Add smooth scroll handler
    navButton.addEventListener('click', (e) => {
      e.preventDefault();
      const targetElement = document.getElementById(sectionId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        console.warn(`Tier Showcase: Target element with id "${sectionId}" not found`);
      }
    });

    // Insert after wishlists-social-proof
    wishlistsProof.insertAdjacentElement('afterend', navButton);
  }

  setupResizeListener() {
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const currentWidth = window.innerWidth;
        const wasMobile = this.state.lastViewportWidth < 768;
        const isMobile = currentWidth < 768;

        // If crossing the 768px breakpoint, re-render carousel
        if (wasMobile !== isMobile) {
          this.state.lastViewportWidth = currentWidth;
          this.updateCarousel();
        } else {
          this.state.lastViewportWidth = currentWidth;
        }
      }, 150);
    };

    window.addEventListener('resize', handleResize);
  }

  init() {
    const configScript = this.querySelector('[data-tier-config]');
    if (!configScript) {
      console.error('Tier Showcase: Config data not found');
      return;
    }

    try {
      this.state.config = JSON.parse(configScript.textContent);
      console.log('Tier Showcase: Config loaded', this.state.config);
      this.render();
    } catch (error) {
      console.error('Tier Showcase: Failed to parse config', error);
    }
  }

  render() {
    const { config } = this.state;
    if (!config || !config.tierEntries || config.tierEntries.length === 0) {
      return;
    }

    const primaryCategories = this.extractUniqueCategories(config.tierEntries, 'primary_category', config.filterOrderByEntry);
    const secondaryCategories = this.extractUniqueCategories(config.tierEntries, 'secondary_category_pill', config.filterOrderByEntry);

    const html = `
      <div class="tier-showcase-wrapper">
        ${this.renderHeader(config)}
        ${this.renderFilters(config, primaryCategories, secondaryCategories)}
        ${this.renderCarousel(config)}
      </div>
    `;

    this.innerHTML = html;
    this.attachEventListeners();
    this.initSwiper();
  }

  renderHeader(config) {
    return `
      <div class="tier-showcase-header">
        <h2 class="tier-showcase-title">${config.sectionHeading || 'Tier Showcase'}</h2>
      </div>
    `;
  }

  renderFilters(config, primaryCategories, secondaryCategories) {
    // Check if either label is defined
    const hasPrimaryLabel = config.primaryCategorisationLabel && config.primaryCategorisationLabel.trim() !== '';
    const hasSecondaryLabel = config.secondaryCategorisationLabel && config.secondaryCategorisationLabel.trim() !== '';

    // If no labels are defined, don't render filters
    if (!hasPrimaryLabel && !hasSecondaryLabel) {
      return '';
    }

    // If categories are empty and labels exist, return empty
    if (primaryCategories.length === 0 && secondaryCategories.length === 0) {
      return '';
    }

    let html = '<div class="tier-showcase-filters page-width">';

    // Render primary filter only if label exists and categories exist
    if (hasPrimaryLabel && primaryCategories.length > 0) {
      const label = config.primaryCategorisationLabel;
      let selectedLabel = '';
      if (this.state.selectedPrimaryFilters.size === 0) {
        selectedLabel = 'All ' + label + 's';
      } else if (this.state.selectedPrimaryFilters.size === 1) {
        selectedLabel = Array.from(this.state.selectedPrimaryFilters)[0];
      } else {
        selectedLabel = `${this.state.selectedPrimaryFilters.size} selected`;
      }
      
      html += `
        <div class="tier-showcase-filter-group" style="position: relative;">
          <button class="tier-showcase-filter-tab-btn" data-filter-type="primary" title="${label}">
            <span class="tier-showcase-filter-label">${label}: <span class="tier-showcase-filter-selected">${selectedLabel}</span></span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          <div class="tier-showcase-filter-dropdown" data-dropdown-type="primary">
            <label class="tier-showcase-filter-option tier-showcase-filter-option--all">
              <input type="checkbox" value="__all__" data-category="__all__" class="tier-showcase-filter-all-checkbox">
              <span>All ${label}s</span>
            </label>
            <div class="tier-showcase-filter-divider"></div>
            ${primaryCategories.map(cat => `
              <label class="tier-showcase-filter-option tier-showcase-filter-option-special">
                <input type="checkbox" value="${cat}" data-category="${cat}" ${this.state.selectedPrimaryFilters.has(cat) ? 'checked' : ''}>
                <span>${cat}</span>
              </label>
            `).join('')}
            <div class="tier-showcase-filter-divider"></div>
            <label style="display: none;" class="tier-showcase-filter-option tier-showcase-filter-option--none">
              <input type="checkbox" value="__none__" data-category="__none__" class="tier-showcase-filter-none-checkbox">
              <span>No ${label}s</span>
            </label>
          </div>
        </div>
      `;
    }

    // Render secondary filter only if label exists and categories exist
    if (hasSecondaryLabel && secondaryCategories.length > 0) {
      const label = config.secondaryCategorisationLabel;
      let selectedLabel = '';
      if (this.state.selectedSecondaryFilters.size === 0) {
        selectedLabel = 'All ' + label + 's';
      } else if (this.state.selectedSecondaryFilters.size === 1) {
        selectedLabel = Array.from(this.state.selectedSecondaryFilters)[0];
      } else {
        selectedLabel = `${this.state.selectedSecondaryFilters.size} selected`;
      }
      
      html += `
        <div class="tier-showcase-filter-group" style="position: relative;">
          <button class="tier-showcase-filter-tab-btn" data-filter-type="secondary" title="${label}">
            <span class="tier-showcase-filter-label">${label}: <span class="tier-showcase-filter-selected">${selectedLabel}</span></span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          <div class="tier-showcase-filter-dropdown" data-dropdown-type="secondary">
            <label class="tier-showcase-filter-option tier-showcase-filter-option--all">
              <input type="checkbox" value="__all__" data-category="__all__" class="tier-showcase-filter-all-checkbox">
              <span>All ${label}s</span>
            </label>
            <div class="tier-showcase-filter-divider"></div>
            ${secondaryCategories.map(cat => `
              <label class="tier-showcase-filter-option tier-showcase-filter-option-special">
                <input type="checkbox" value="${cat}" data-category="${cat}" ${this.state.selectedSecondaryFilters.has(cat) ? 'checked' : ''}>
                <span>${cat}</span>
              </label>
            `).join('')}
            <div class="tier-showcase-filter-divider"></div>
            <label style="display: none;" class="tier-showcase-filter-option tier-showcase-filter-option--none">
              <input type="checkbox" value="__none__" data-category="__none__" class="tier-showcase-filter-none-checkbox">
              <span>No ${label}s</span>
            </label>
          </div>
        </div>
      `;
    }

    const hasTertiaryLabel = config.tertiaryCategorisationLabel && config.tertiaryCategorisationLabel.trim() !== '';
    const tertiaryCategories = this.extractUniqueCategories(config.tierEntries, 'tertiary_category_sort', config.filterOrderByEntry);

    if (hasTertiaryLabel && tertiaryCategories.length > 0) {
      const label = config.tertiaryCategorisationLabel;
      let selectedLabel = '';
      if (this.state.selectedTertiaryFilters.size === 0) {
        selectedLabel = 'All ' + label + 's';
      } else if (this.state.selectedTertiaryFilters.size === 1) {
        selectedLabel = Array.from(this.state.selectedTertiaryFilters)[0];
      } else {
        selectedLabel = `${this.state.selectedTertiaryFilters.size} selected`;
      }

      html += `
        <div class="tier-showcase-filter-group" style="position: relative;">
          <button class="tier-showcase-filter-tab-btn" data-filter-type="tertiary" title="${label}">
            <span class="tier-showcase-filter-label">${label}: <span class="tier-showcase-filter-selected">${selectedLabel}</span></span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          <div class="tier-showcase-filter-dropdown" data-dropdown-type="tertiary">
            <label class="tier-showcase-filter-option tier-showcase-filter-option--all">
              <input type="checkbox" value="__all__" data-category="__all__" class="tier-showcase-filter-all-checkbox">
              <span>All ${label}s</span>
            </label>
            <div class="tier-showcase-filter-divider"></div>
            ${tertiaryCategories.map(cat => `
              <label class="tier-showcase-filter-option tier-showcase-filter-option-special">
                <input type="checkbox" value="${cat}" data-category="${cat}" ${this.state.selectedTertiaryFilters.has(cat) ? 'checked' : ''}>
                <span>${cat}</span>
              </label>
            `).join('')}
            <div class="tier-showcase-filter-divider"></div>
            <label style="display: none;" class="tier-showcase-filter-option tier-showcase-filter-option--none">
              <input type="checkbox" value="__none__" data-category="__none__" class="tier-showcase-filter-none-checkbox">
              <span>No ${label}s</span>
            </label>
          </div>
        </div>
      `;
    }

    html += '</div>';
    return html;
  }

  renderCarousel(config) {
    const { tierEntries, displayDescriptions } = config;

    if (!tierEntries || tierEntries.length === 0) {
      return '<div class="tier-showcase-no-results"><p class="tier-showcase-no-results-text">No items available</p></div>';
    }

    const filteredItems = this.getFilteredItems();

    if (filteredItems.length === 0) {
      return '<div class="tier-showcase-no-results"><p class="tier-showcase-no-results-text">No items match your filters</p></div>';
    }

    this.state.filteredItems = filteredItems;

    // Render grid items
    const itemsHtml = filteredItems.map(item => `
      <div class="tier-showcase-grid-item">
        <div class="tier-showcase-carousel-item">
          ${item.image ? `
            <div class="tier-showcase-carousel-item-image">
              <img src="${item.image}" alt="${item.entry_name_displayed || item.primary_category}" loading="lazy">
            </div>
          ` : ''}
          <div class="tier-showcase-carousel-item-badges">
            ${item.secondary_category_pill ? 
              item.secondary_category_pill.split(',').map(pill => {
                const key = pill.trim().toLowerCase();
                const colour = SECONDARY_CATEGORY_COLOURS[key] || '';
                return `<span class="tier-showcase-carousel-item-category secondary" style="background: ${colour};">${pill.trim()}</span>`;
              }).join('')
            : ''}
          </div>
          ${displayDescriptions ? `
          <div class="tier-showcase-carousel-item-content">
            <div class="tier-showcase-carousel-item-categories">
              ${config.showEntryNameInsteadOfCategory
                ? (item.entry_name_displayed ? `<span class="tier-showcase-carousel-item-category primary">${item.entry_name_displayed}</span>` : '')
                : (item.primary_category
                    ? item.primary_category.split(',').map(cat => `<span class="tier-showcase-carousel-item-category primary">${cat.trim()}</span>`).join('')
                    : '')
              }
            </div>
            ${item.description ? `<span class="tier-showcase-carousel-item-description">${item.description}</span>` : ''}
          </div>
          ` : ''}
        </div>
      </div>
    `).join('');

    return `
      <div class="tier-showcase-carousel-wrapper page-width">
        <div class="tier-showcase-carousel-grid" style="--tier-cols: ${this.state.colsPerRow};">
          ${itemsHtml}
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    // Filter tab buttons (toggle dropdown)
    this.querySelectorAll('.tier-showcase-filter-tab-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleFilterTabToggle(e));
    });

    // Filter checkboxes
    this.querySelectorAll('.tier-showcase-filter-option input[type="checkbox"]').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => this.handleFilterCheckboxChange(e));
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.tier-showcase-filter-group')) {
        this.querySelectorAll('.tier-showcase-filter-dropdown').forEach(dropdown => {
          dropdown.classList.remove('active');
        });
        this.querySelectorAll('.tier-showcase-filter-tab-btn').forEach(btn => {
          btn.classList.remove('active');
        });
      }
    });
  }

  initSwiper() {
    // Swiper is no longer used - grid layout is used instead
    // This method is kept for backward compatibility
  }

  handleFilterTabToggle(e) {
    e.stopPropagation();
    const btn = e.currentTarget;
    const filterType = btn.dataset.filterType;
    const dropdown = this.querySelector(`[data-dropdown-type="${filterType}"]`);
    
    // Close all other dropdowns
    this.querySelectorAll('.tier-showcase-filter-dropdown').forEach(d => {
      if (d !== dropdown) d.classList.remove('active');
    });
    this.querySelectorAll('.tier-showcase-filter-tab-btn').forEach(b => {
      if (b !== btn) b.classList.remove('active');
    });

    // Toggle this dropdown
    dropdown.classList.toggle('active');
    btn.classList.toggle('active');
  }

  handleFilterCheckboxChange(e) {
    const checkbox = e.currentTarget;
    const filterType = checkbox.closest('[data-dropdown-type]')?.dataset.dropdownType;
    const category = checkbox.value;

    if (!filterType) return;

    const filterSet = filterType === 'primary'
      ? this.state.selectedPrimaryFilters
      : filterType === 'secondary'
        ? this.state.selectedSecondaryFilters
        : this.state.selectedTertiaryFilters;

    const categories = filterType === 'primary'
      ? this.extractUniqueCategories(this.state.config.tierEntries, 'primary_category', this.state.config.filterOrderByEntry)
      : filterType === 'secondary'
        ? this.extractUniqueCategories(this.state.config.tierEntries, 'secondary_category_pill', this.state.config.filterOrderByEntry)
        : this.extractUniqueCategories(this.state.config.tierEntries, 'tertiary_category_sort', this.state.config.filterOrderByEntry);


    const dropdown = checkbox.closest('[data-dropdown-type]');
    const allCheckbox = dropdown.querySelector('.tier-showcase-filter-all-checkbox');
    const noneCheckbox = dropdown.querySelector('.tier-showcase-filter-none-checkbox');

    // Handle "All Characters" option
    if (category === '__all__') {
      if (checkbox.checked) {
        // Check all individual categories
        filterSet.clear();
        categories.forEach(cat => filterSet.add(cat));
        // Check all individual checkboxes
        dropdown.querySelectorAll('input[type="checkbox"]:not(.tier-showcase-filter-all-checkbox):not(.tier-showcase-filter-none-checkbox)').forEach(cb => {
          cb.checked = true;
        });
        // Uncheck "No Characters"
        noneCheckbox.checked = false;
      } else {
        // Uncheck all
        filterSet.clear();
        dropdown.querySelectorAll('input[type="checkbox"]:not(.tier-showcase-filter-all-checkbox):not(.tier-showcase-filter-none-checkbox)').forEach(cb => {
          cb.checked = false;
        });
      }
    }
    // Handle "No Characters" option
    else if (category === '__none__') {
      if (checkbox.checked) {
        filterSet.clear();
        // Uncheck all other checkboxes
        dropdown.querySelectorAll('input[type="checkbox"]').forEach(cb => {
          if (cb !== checkbox) cb.checked = false;
        });
        // Uncheck "All Characters"
        allCheckbox.checked = false;
      } else {
        // If unchecking "No Characters", do nothing specific
      }
    }
    // Handle regular category options
    else {
      if (checkbox.checked) {
        filterSet.add(category);
        // Uncheck "No Characters" if any category is checked
        noneCheckbox.checked = false;
      } else {
        filterSet.delete(category);
        // Uncheck "All Characters" if any category is unchecked
        allCheckbox.checked = false;
      }

      // Check if all categories are now selected
      const allSelected = categories.every(cat => filterSet.has(cat));
      if (allSelected && filterSet.size > 0) {
        allCheckbox.checked = true;
      } else {
        allCheckbox.checked = false;
      }
    }

    // Update the tab button label
    const btn = this.querySelector(`[data-filter-type="${filterType}"]`);
    if (btn) {
      let selectedLabel = '';
      const label = filterType === 'primary'
        ? this.state.config.primaryCategorisationLabel
        : filterType === 'secondary'
          ? this.state.config.secondaryCategorisationLabel
          : this.state.config.tertiaryCategorisationLabel;

      
      if (noneCheckbox.checked) {
        selectedLabel = `No ${label}`;
      } else if (allCheckbox.checked || filterSet.size === 0) {
        selectedLabel = `All ${label}s`;
      } else if (filterSet.size === 1) {
        selectedLabel = Array.from(filterSet)[0];
      } else {
        selectedLabel = `${filterSet.size} selected`;
      }

      const labelSpan = btn.querySelector('span');
      labelSpan.innerHTML = `${label}: <span class="tier-showcase-filter-selected">${selectedLabel}</span>`;
    }

    // Re-render carousel with new filters
    this.updateCarousel();
  }

  getFilteredItems() {
    const { config } = this.state;
    const { tierEntries } = config;

    if (!tierEntries || tierEntries.length === 0) return [];

    const hasPrimaryFilters = this.state.selectedPrimaryFilters.size > 0;
    const hasSecondaryFilters = this.state.selectedSecondaryFilters.size > 0;
    const hasTertiaryFilters = this.state.selectedTertiaryFilters.size > 0;


    let items = tierEntries;

    // If filters are selected, filter items
    if (hasPrimaryFilters || hasSecondaryFilters || hasTertiaryFilters) {
      items = tierEntries.filter(item => {
        let matchesPrimary = true;
        let matchesSecondary = true;

        // Handle primary filters
        if (hasPrimaryFilters) {
          if (this.state.selectedPrimaryFilters.has('__none__')) {
            // Show items where primary_category is empty
            matchesPrimary = !item.primary_category || item.primary_category.trim() === '';
          } else {
            // Show items matching ANY of the selected categories (OR logic)
          const primaryValues = item.primary_category
            ? item.primary_category.split(',').map(v => v.trim())
            : [];
          matchesPrimary = Array.from(this.state.selectedPrimaryFilters).some(cat =>
            primaryValues.includes(cat)
          );
          }
        }

        // Handle secondary filters
        if (hasSecondaryFilters) {
          if (this.state.selectedSecondaryFilters.has('__none__')) {
            // Show items where secondary_category_pill is empty
            matchesSecondary = !item.secondary_category_pill || item.secondary_category_pill.trim() === '';
          } else {
            // Show items matching ANY of the selected categories (OR logic)
            const secondaryValues = item.secondary_category_pill
              ? item.secondary_category_pill.split(',').map(v => v.trim())
              : [];
            matchesSecondary = Array.from(this.state.selectedSecondaryFilters).some(cat =>
              secondaryValues.includes(cat)
            );
          }
        }

        let matchesTertiary = true;
        if (hasTertiaryFilters) {
          if (this.state.selectedTertiaryFilters.has('__none__')) {
            matchesTertiary = !item.tertiary_category_sort || item.tertiary_category_sort.trim() === '';
          } else {
            const tertiaryValues = item.tertiary_category_sort
              ? item.tertiary_category_sort.split(',').map(v => v.trim())
              : [];
            matchesTertiary = Array.from(this.state.selectedTertiaryFilters).some(cat =>
              tertiaryValues.includes(cat)
            );
          }
        }

        return matchesPrimary && matchesSecondary && matchesTertiary;

      });
    }

    console.log('Filter debug:', {
      selectedSecondary: Array.from(this.state.selectedSecondaryFilters),
      items: items.map(item => ({
        name: item.entry_name_displayed,
        secondaryRaw: item.secondary_category_pill,
        secondaryValues: item.secondary_category_pill 
          ? item.secondary_category_pill.split(',').map(v => v.trim()) 
          : []
      }))
    });

    // Sort by priority first, then alphabetically by entry_name_displayed
    return items.sort((a, b) => {
      const priorityA = parseInt(a.priority) || 0;
      const priorityB = parseInt(b.priority) || 0;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      const tertiaryA = (a.tertiary_category_sort || '').toLowerCase();
      const tertiaryB = (b.tertiary_category_sort || '').toLowerCase();
      return tertiaryA.localeCompare(tertiaryB);
    });
  }

  updateCarousel() {
    // Find either the carousel wrapper or the no-results container
    let container = this.querySelector('.tier-showcase-carousel-wrapper');
    
    if (!container) {
      container = this.querySelector('.tier-showcase-no-results');
    }
    
    if (!container) return;

    const newCarouselHTML = this.renderCarousel(this.state.config);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = newCarouselHTML;
    
    container.replaceWith(tempDiv.firstElementChild);
  }

  extractUniqueCategories(items, field, ordered = false) {
    const categories = new Set();
    items.forEach(item => {
      if (item[field]) {
        item[field].split(',').forEach(val => {
          const trimmed = val.trim();
          if (trimmed) categories.add(trimmed);
        });
      }
    });
    const result = Array.from(categories).filter(cat => cat && cat.trim() !== '');
    return ordered ? result : result.sort();
  }
}

customElements.define('tier-showcase', TierShowcase);
